import carbonApi from "@/submodules/carbon-plugin/api";

function unwrap(response, { defaultMessage } = {}) {
    if (!response) {
        throw new Error(defaultMessage || "응답이 존재하지 않습니다.");
    }

    const payload = response.data ?? response;

    if (payload?.success === false) {
        const message =
            payload?.message || payload?.error?.message || defaultMessage || "요청 처리 중 오류가 발생했습니다.";
        const error = new Error(message);
        if (payload?.error?.code) {
            error.code = payload.error.code;
        }
        error.payload = payload;
        throw error;
    }

    // 성공 시 data와 message 함께 반환
    return {
        data: payload?.data ?? payload ?? null,
        message: payload?.message || null,
        success: payload?.success ?? true,
    };
}

export async function fetchOrganizations() {
    const orgRes = await carbonApi.organization.getOrgs();
    const result = unwrap(orgRes, { defaultMessage: "조직 목록을 불러오지 못했습니다." });
    return result.data?.organizations || [];
}

export async function fetchOrgBoundaries(orgCode) {
    if (!orgCode) {
        throw new Error("조직 코드는 필수입니다.");
    }

    const [orgBoundaryRes, operBoundaryRes] = await Promise.all([
        carbonApi.orgBoundary.getOrgBoundaries(orgCode, "active"),
        carbonApi.operBoundary.getOperBoundaries(orgCode),
    ]);

    const orgBoundaryResult = unwrap(orgBoundaryRes, { defaultMessage: "조직 경계를 불러오지 못했습니다." });
    const operBoundaryResult = unwrap(operBoundaryRes, { defaultMessage: "운영 경계를 불러오지 못했습니다." });

    return {
        orgBoundaries: orgBoundaryResult.data?.boundaries || orgBoundaryResult.data?.data || [],
        operBoundaries: operBoundaryResult.data?.boundaries || operBoundaryResult.data?.data || [],
    };
}

export async function fetchOrgContext(orgCode) {
    const organizations = await fetchOrganizations();

    if (!organizations.length) {
        throw new Error("조직 정보를 불러오지 못했습니다.");
    }

    const selectedOrg =
        (orgCode && organizations.find((org) => org.orgCode === orgCode || org.orgIdx === orgCode)) ||
        organizations.find((org) => org.status === "ACTIVE") ||
        organizations[0];

    if (!selectedOrg) {
        throw new Error("유효한 조직을 찾을 수 없습니다.");
    }

    const code = selectedOrg.orgCode || selectedOrg.orgIdx;
    const { orgBoundaries, operBoundaries } = await fetchOrgBoundaries(code);

    return {
        organizations,
        organization: selectedOrg,
        orgCode: code,
        orgBoundaries,
        operBoundaries,
    };
}

export async function fetchFactorVersions() {
    const response = await carbonApi.emissionFactor.getVersions();
    const result = unwrap(response, { defaultMessage: "배출계수 버전을 불러오지 못했습니다." });
    return result.data?.versions || [];
}

export async function runCalculation(payload) {
    const response = await carbonApi.calculation.runCalculation(payload);
    const result = unwrap(response, { defaultMessage: "배출량 계산 실행에 실패했습니다." });
    return result;
}

/**
 * 인벤토리 생성 (서버: 단일 period YYYYMM)
 * @param {Object} payload - { orgBoundaryIdx, operBoundaryIdx, period, versionIdx?, recalculate? }
 */
export async function createInventory(payload) {
    const response = await carbonApi.inventory.createInventory(payload);
    const result = unwrap(response, { defaultMessage: "인벤토리 생성에 실패했습니다." });
    return {
        success: result.success,
        message: result.message,
        inventory: result.data,
    };
}

export async function fetchInventory(inventoryIdx) {
    const response = await carbonApi.inventory.getInventory(inventoryIdx);
    const result = unwrap(response, { defaultMessage: "인벤토리 조회에 실패했습니다." });
    return result.data;
}

export async function deleteInventory(inventoryIdx) {
    const response = await carbonApi.inventory.deleteInventory(inventoryIdx);
    const result = unwrap(response, { defaultMessage: "인벤토리 삭제에 실패했습니다." });
    return {
        success: result.success,
        message: result.message,
    };
}

export async function fetchInventoriesList(params = {}) {
    const response = await carbonApi.inventory.getInventories(params);
    const result = unwrap(response, { defaultMessage: "인벤토리 목록을 불러오지 못했습니다." });

    if (Array.isArray(result.data?.data)) {
        return {
            items: result.data.data,
            pagination: result.data.pagination || null,
        };
    }

    return {
        items: result.data?.inventories || result.data || [],
        pagination: result.data?.pagination || null,
    };
}

export async function fetchInventoryTrends(params) {
    const response = await carbonApi.inventory.getInventoryTrends(params);
    const result = unwrap(response, { defaultMessage: "인벤토리 추이를 불러오지 못했습니다." });
    return result.data?.trends || result.data || [];
}

export async function compareInventories(payload) {
    const response = await carbonApi.inventory.compareInventories(payload.inventory1, payload.inventory2);
    const result = unwrap(response, { defaultMessage: "인벤토리 비교에 실패했습니다." });
    return result.data;
}

export async function fetchQualityReport(inventoryIdx) {
    const response = await carbonApi.inventory.checkInventoryQuality(inventoryIdx);
    const result = unwrap(response, { defaultMessage: "품질 점검 결과를 불러오지 못했습니다." });
    return result.data;
}

export async function fetchUncertainty(inventoryIdx) {
    const response = await carbonApi.inventory.assessUncertainty(inventoryIdx);
    const result = unwrap(response, { defaultMessage: "불확도 평가 결과를 불러오지 못했습니다." });
    return result.data;
}

export function toPeriodString(value) {
    if (!value) return "";
    if (/^\d{6}$/.test(value)) return value;
    return value.replace(/[^0-9]/g, "").slice(0, 6);
}

export function toDisplayPeriod(value) {
    if (!value) return "-";
    const period = toPeriodString(value);
    if (period.length !== 6) return value;
    return `${period.slice(0, 4)}-${period.slice(4, 6)}`;
}

export async function fetchFacility(facilityCode) {
    const response = await carbonApi.utility.getFacilities(facilityCode);
    const result = unwrap(response, { defaultMessage: "시설 조회에 실패했습니다." });
    return result.data;
}

export async function fetchOrgBoundary(orgBoundaryIdx) {
    const response = await carbonApi.orgBoundary.getOrgBoundary(orgBoundaryIdx);
    const result = unwrap(response, { defaultMessage: "조직 경계 조회에 실패했습니다." });
    return result.data;
}

export async function fetchOperBoundary(operBoundaryIdx) {
    const response = await carbonApi.operBoundary.getOperBoundary(operBoundaryIdx);
    const result = unwrap(response, { defaultMessage: "운영 경계 조회에 실패했습니다." });
    return result.data;
}

export function buildCsvContent(lines = []) {
    const headers = [
        "기간",
        "Scope",
        "시설",
        "카테고리",
        "활동 건수",
        "조정 배출량 (tCO₂e)",
        "포함 비율 (%)",
        "계산식",
    ];

    const rows = lines.map((line) => [
        toDisplayPeriod(line.period),
        line.scope,
        line.facilityIdx,
        line.categoryCode,
        line.activityCount,
        line.co2eqAmount ?? line.resultEmission ?? line.adjustedEmission,
        line.includePct,
        line.calculationFormula,
    ]);

    return [headers, ...rows]
        .map((row) =>
            row
                .map((col) => {
                    if (col == null) return "";
                    const value = String(col);
                    return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
                })
                .join(",")
        )
        .join("\n");
}
